import React, { useMemo, useState } from 'react';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';
import { RtkQueryTable, RtkQueryTableModels } from '../../components/Table/RtkQueryTable';
import { FakeButtonLink } from '../../components/FakeLink';
import { converTableModelsToApiArgs } from '../../helpers/api';
import { usePermissions } from '../../hooks';
import { useFetchUserGiftsQuery, useUpdateUserGiftMutation } from '../../redux/features/userGift/userGift.apiSlice';
import { locations } from '../../routes/locations';
import { GiftTargetType, UserGift, UserGiftStatus } from '../../types/userGift';
import { formatDateTime } from '../../utils/date';
import * as permissions from '../../utils/permissions';
import { AddUserGiftDialog } from './AddUserGiftDialog';
import { EditUserGiftDialog } from './EditUserGiftDialog';
import { describeGiftError, GIFT_TYPE_LABELS, isExpired, TARGET_TYPE_LABELS } from './userGiftCopy';

const renderUserLink = (userId?: number | string | null) => {
  if (!userId) {
    return '';
  }

  return (
    <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.user(Number(userId))}>
      {userId}
    </FakeButtonLink>
  );
};

/**
 * Gifts for a person, by user id, national id, email or phone. The person doesn't need an account yet: the gift
 * applies once they sign up and subscribe.
 */
export function UserGiftList() {
  const { enqueueSnackbar } = useSnackbar();
  const canCreate = usePermissions({ items: [permissions.UserGift.create] });
  const canUpdate = usePermissions({ items: [permissions.UserGift.update] });
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editing, setEditing] = useState<UserGift | null>(null);
  const [tableModels, setTableModels] = useState<RtkQueryTableModels>({
    paginationModel: { page: 0, pageSize: 50 },
    sortModel: [{ field: 'createdAt', sort: 'desc' }],
  });
  const { data, isFetching, isError } = useFetchUserGiftsQuery(converTableModelsToApiArgs(tableModels));
  const [updateGift, { isLoading: isUpdating }] = useUpdateUserGiftMutation();

  const columns = useMemo<GridColDef<UserGift>[]>(() => {
    const toggleStatus = async (gift: UserGift) => {
      const status = gift.status === UserGiftStatus.ACTIVE ? UserGiftStatus.DISABLED : UserGiftStatus.ACTIVE;

      try {
        await updateGift({ id: gift.id, status }).unwrap();
        enqueueSnackbar(status === UserGiftStatus.ACTIVE ? 'Gift enabled.' : 'Gift disabled.', {
          variant: 'success',
        });
      } catch (err) {
        enqueueSnackbar(describeGiftError(err), { variant: 'error' });
      }
    };

    return [
      ...(canUpdate
        ? [
            {
              field: 'actions',
              type: 'actions',
              headerName: 'Actions',
              width: 90,
              getActions: ({ row }: GridRowParams<UserGift>) => [
                <GridActionsCellItem
                  key={`user-gift-edit-${row.id}`}
                  icon={<EditOutlinedIcon />}
                  label="Edit"
                  onClick={() => setEditing(row)}
                />,
                <GridActionsCellItem
                  key={`user-gift-toggle-${row.id}`}
                  icon={row.status === UserGiftStatus.ACTIVE ? <BlockIcon /> : <CheckCircleOutlineIcon />}
                  label={row.status === UserGiftStatus.ACTIVE ? 'Disable' : 'Enable'}
                  onClick={() => toggleStatus(row)}
                />,
              ],
            } as GridColDef<UserGift>,
          ]
        : []),
      { field: 'id', headerName: 'Id', type: 'number', width: 80 },
      {
        field: 'giftType',
        headerName: 'Type',
        width: 180,
        sortable: false,
        filterable: false,
        valueGetter: ({ row }) => GIFT_TYPE_LABELS[row.giftType?.type] || row.giftType?.type,
      },
      {
        field: 'premiumMonths',
        headerName: 'Premium months',
        type: 'number',
        width: 140,
        sortable: false,
        filterable: false,
        valueGetter: ({ value }) => value ?? null,
      },
      {
        field: 'targetType',
        headerName: 'Target',
        width: 130,
        type: 'singleSelect',
        valueOptions: Object.values(GiftTargetType).map((value) => ({ value, label: TARGET_TYPE_LABELS[value] })),
      },
      {
        field: 'targetValue',
        headerName: 'Value',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => {
          if (row.targetType === GiftTargetType.USER_ID) {
            return renderUserLink(row.targetValue);
          }

          return <code>{row.targetValue}</code>;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        type: 'singleSelect',
        valueOptions: Object.values(UserGiftStatus),
        renderCell: ({ row }) => {
          if (row.status === UserGiftStatus.DISABLED) {
            return <Chip size="small" label="Disabled" />;
          }

          return isExpired(row.expiresAt) ? (
            <Chip size="small" color="warning" label="Expired" />
          ) : (
            <Chip size="small" color="success" label="Active" />
          );
        },
      },
      {
        field: 'expiresAt',
        headerName: 'Expires at',
        type: 'dateTime',
        width: 180,
        valueGetter: ({ value }) => (value ? new Date(value) : null),
      },
      {
        field: 'usedByUserId',
        headerName: 'Used by user',
        type: 'number',
        width: 130,
        renderCell: ({ value }) => renderUserLink(value),
      },
      {
        field: 'firstUsedAt',
        headerName: 'First used',
        type: 'dateTime',
        width: 180,
        valueGetter: ({ value }) => (value ? new Date(value) : null),
      },
      {
        field: 'lastUsedAt',
        headerName: 'Last used',
        type: 'dateTime',
        width: 180,
        valueGetter: ({ value }) => (value ? new Date(value) : null),
      },
      { field: 'note', headerName: 'Note', width: 200, sortable: false },
      { field: 'createdByEmployeeName', headerName: 'Created by', width: 150, sortable: false },
      {
        field: 'createdAt',
        headerName: 'Created at',
        width: 180,
        filterable: false,
        valueFormatter: ({ value }) => (value ? formatDateTime(value) : ''),
      },
    ];
  }, [canUpdate, updateGift, enqueueSnackbar]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 3 }}>
        <Typography variant="h4">
          Gifts &nbsp;
          {canCreate && (
            <Fab color="primary" size="small" aria-label="Add gift" onClick={() => setIsAddOpen(true)}>
              <AddIcon />
            </Fab>
          )}
        </Typography>
        <Typography color="text.secondary">
          Free management fee for a person, by user id, national id, email or phone. A portfolio opened before the gift
          expires never pays a management fee.
        </Typography>
      </Box>
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the gifts.
        </Alert>
      )}
      <RtkQueryTable
        models={tableModels}
        onModelsChange={setTableModels}
        rows={data?.items || []}
        columns={columns}
        loading={isFetching || isUpdating}
        rowCount={data?.totalItems || 0}
        initialSortModel={[{ field: 'createdAt', sort: 'desc' }]}
      />
      <AddUserGiftDialog open={isAddOpen} onClose={() => setIsAddOpen(false)} />
      <EditUserGiftDialog gift={editing} onClose={() => setEditing(null)} />
    </Container>
  );
}
